import { useLazyQuery, useMutation } from '@apollo/client';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { get } from 'lodash';

import { useAnalytics } from '../../context/AnalyticsProvider';
import type { Country } from '../../entities/Country';
import type { Currency } from '../../entities/Currency';
import type { PaymentMethodType, UseCase } from '../../entities/enums';
import {
  GET_STRIPE_SAVED_CREDIT_CARDS,
  PAY_BY_STRIPE_SAVED_CREDIT_CARD,
  DELETE_STRIPE_SAVED_CREDIT_CARD,
  PAY_INVOICE_BY_STRIPE_SAVED_CREDIT_CARD,
} from '../../graphql';
import { useGenerateIdempotencyKey } from '../../hook/useGenerateIdempotencyKey';
import translations from '../../locales.vocab';
import type { HirerPaymentResult, Invoice, PaymentStatus } from '../../types';
import {
  CreditCardFormNotValidError,
  PaymentConfirmationError,
  PaymentSavedCreditCardPayError,
  PaymentDefaultError,
} from '../Errors/customErrors';
import { hideCreditCardAtom } from '../Maintenance/MaintenanceStore';
import { selectedCardBrandAtom } from '../Surcharges/SurchargeStore';
import useBrandSurcharge from '../Surcharges/useBrandSurcharge';

import {
  deleteSavedCardDialogOpenAtom,
  isDeleteSaveCardLoadingAtom,
  savedCardInfoAtom,
} from './CreditCardStore';
import { getSavedCardBrand } from './getCreditCardBrand';

interface CajaInputs {
  orderId: string;
}

interface InvoiceInputs {
  customerId: string;
  prn: string;
  invoice: Invoice[];
  email: string;
  totalForPaymentIntent: number;
  currency: Currency;
}

export interface UseSavedCreditCardInput<T> {
  advertiserId?: string; // being used by invoice flow
  country: Country;
  onError: (error: any, paymentMethod?: PaymentMethodType) => void;
  paymentMethod: PaymentMethodType;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

const useSavedCreditCard = ({
  country,
  onError,
  paymentMethod,
  advertiserId,
  invoiceInputs = {
    customerId: '',
    prn: '',
    invoice: [],
    email: '',
    totalForPaymentIntent: 0,
    currency: 'AUD',
  },
  cajaInputs = {
    orderId: '',
  },
}: UseSavedCreditCardInput<UseCase>) => {
  const { customerId, prn, invoice, email, totalForPaymentIntent, currency } =
    invoiceInputs;
  const { orderId } = cajaInputs;

  const stripe = useStripe();
  const elements = useElements();
  const showToast = useToast();
  const { idempotencyKey, generateNewIdempotencyKey } =
    useGenerateIdempotencyKey();

  const { t } = useTranslations(translations);

  const { sendLinkDeleteCardButtonEvent } = useAnalytics();

  const { pickBrand } = useBrandSurcharge();

  const selectedCardBrand = useAtomValue(selectedCardBrandAtom);

  const [savedCardInfo, setSavedCardInfo] = useAtom(savedCardInfoAtom);
  const setDeleteSaveCardDialog = useSetAtom(deleteSavedCardDialogOpenAtom);
  const [isDeleteSaveCardLoading, setIsDeleteSaveCardLoading] = useAtom(
    isDeleteSaveCardLoadingAtom,
  );
  const isMaintenance = useAtomValue(hideCreditCardAtom);

  const [getSavedCards, { loading: isGetSavedCardsLoading }] = useLazyQuery(
    GET_STRIPE_SAVED_CREDIT_CARDS,
    {
      variables: { countryCode: country },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const cards = get(data, 'SavedStripeCreditCards') || [];
        const info = cards.length > 0 ? cards[0] : undefined;
        setSavedCardInfo(info);
        if (info) {
          pickBrand(getSavedCardBrand(info.brand));
        }
      },
      onError: (error) => {
        if (onError) {
          onError(error, paymentMethod);
        }
        setSavedCardInfo(undefined);
      },
    },
  );

  // from schema - payments
  const [payByStripeSavedCreditCardMutation] = useMutation(
    PAY_BY_STRIPE_SAVED_CREDIT_CARD,
    {
      variables: {
        creditCardBrand: selectedCardBrand,
        orderId,
        paymentMethodId: savedCardInfo?.token,
      },
    },
  );

  const invoices = invoice.map(({ id, ...rest }) => ({
    reference: id,
    ...rest,
  }));

  const [payByStripeSavedCreditCardForInvoice] = useMutation(
    PAY_INVOICE_BY_STRIPE_SAVED_CREDIT_CARD,
    {
      variables: {
        creditCardBrand: selectedCardBrand,
        seekBillingAccountId: customerId,
        seekAdvertiserId: advertiserId,
        prn,
        totalForPaymentIntent,
        invoices,
        email,
        paymentMethodId: savedCardInfo?.token,
        paymentCurrency: currency,
        idempotencyKey,
      },
    },
  );

  const [deleteStripeSavedCreditCardMutation] = useMutation(
    DELETE_STRIPE_SAVED_CREDIT_CARD,
    {
      variables: {
        advertiserNation: country,
        paymentMethodId: savedCardInfo?.token,
      },
    },
  );

  const submitSavedCreditCardPayment =
    async (): Promise<HirerPaymentResult> => {
      if (!stripe || !elements) {
        throw new PaymentDefaultError('Stripe or Elements is not loaded');
      }
      if (isMaintenance) {
        throw new PaymentDefaultError('Payment method is in maintenance');
      }
      if (savedCardInfo === undefined) {
        throw new CreditCardFormNotValidError();
      }

      const paymentResponse = await payByStripeSavedCreditCardMutation();

      const payBySavedCreditCardPayload =
        paymentResponse?.data?.payByStripeSavedCreditCard;

      if (!payBySavedCreditCardPayload) {
        throw new PaymentDefaultError('Unexpected undefined response payload');
      }

      if (
        payBySavedCreditCardPayload.__typename ===
        'SavedCreditCardPaymentSuccessPayload'
      ) {
        const stripeResponse = await stripe.confirmCardPayment(
          payBySavedCreditCardPayload.clientSecret,
          {
            payment_method: savedCardInfo?.token,
          },
        );

        if (stripeResponse.error?.payment_intent?.status === 'succeeded') {
          return {
            paymentIntentId: stripeResponse.error?.payment_intent?.id,
            status: 'succeeded' as PaymentStatus,
            paymentMethod: 'CreditCard',
          };
        }

        if (stripeResponse.error) {
          throw new PaymentConfirmationError(
            stripeResponse.error.message ?? 'Stripe error',
            stripeResponse.error.type,
            stripeResponse.error.code,
            stripeResponse.error.decline_code,
          );
        }

        return {
          paymentIntentId: stripeResponse.paymentIntent.id,
          status: stripeResponse.paymentIntent.status,
          paymentMethod: 'CreditCard',
        };
      }

      if (
        payBySavedCreditCardPayload.__typename === 'SavedCreditCardPaymentError'
      ) {
        throw new PaymentSavedCreditCardPayError(
          payBySavedCreditCardPayload.message,
          payBySavedCreditCardPayload.paymentIntentId,
          payBySavedCreditCardPayload.paymentIntentStatus,
          payBySavedCreditCardPayload.errorCode ?? undefined,
          payBySavedCreditCardPayload.declineCode ?? undefined,
          payBySavedCreditCardPayload.param ?? undefined,
        );
      }

      throw new PaymentDefaultError(payBySavedCreditCardPayload.message);
    };

  const submitSavedCreditCardPaymentForInvoice =
    async (): Promise<HirerPaymentResult> => {
      if (!stripe || !elements) {
        throw new PaymentDefaultError('Stripe or Elements is not loaded');
      }
      if (isMaintenance) {
        throw new PaymentDefaultError('Payment method is in maintenance');
      }
      if (savedCardInfo === undefined) {
        throw new CreditCardFormNotValidError();
      }

      const paymentResponse = await payByStripeSavedCreditCardForInvoice();

      const payBySavedCreditCardPayload =
        paymentResponse?.data?.payByStripeSavedCreditCardForInvoice;

      if (!payBySavedCreditCardPayload) {
        throw new PaymentDefaultError('Unexpected undefined response payload');
      }

      if (
        payBySavedCreditCardPayload.__typename ===
        'SavedCreditCardPaymentSuccessPayload'
      ) {
        const stripeResponse = await stripe.confirmCardPayment(
          payBySavedCreditCardPayload.clientSecret,
          {
            payment_method: savedCardInfo?.token,
          },
        );

        if (stripeResponse.error?.payment_intent?.status === 'succeeded') {
          return {
            paymentIntentId: stripeResponse.error?.payment_intent?.id,
            status: 'succeeded' as PaymentStatus,
            paymentMethod: 'CreditCard',
          };
        }

        if (stripeResponse.error) {
          throw new PaymentConfirmationError(
            stripeResponse.error.message ?? 'Stripe error',
            stripeResponse.error.type,
            stripeResponse.error.code,
            stripeResponse.error.decline_code,
          );
        }

        return {
          paymentIntentId: stripeResponse.paymentIntent.id,
          status: stripeResponse.paymentIntent.status,
          paymentMethod: 'CreditCard',
        };
      }

      if (
        payBySavedCreditCardPayload.__typename === 'SavedCreditCardPaymentError'
      ) {
        throw new PaymentSavedCreditCardPayError(
          payBySavedCreditCardPayload.message,
          payBySavedCreditCardPayload.paymentIntentId,
          payBySavedCreditCardPayload.paymentIntentStatus,
          payBySavedCreditCardPayload.errorCode ?? undefined,
          payBySavedCreditCardPayload.declineCode ?? undefined,
          payBySavedCreditCardPayload.param ?? undefined,
        );
      }

      throw new PaymentDefaultError(payBySavedCreditCardPayload.message);
    };

  const onDeleteSavedCard = async () => {
    generateNewIdempotencyKey();
    setIsDeleteSaveCardLoading(true);

    await deleteStripeSavedCreditCardMutation();

    sendLinkDeleteCardButtonEvent();

    showToast({
      tone: 'positive',
      message: '',
      description: t('stripe.form.card.remove'),
    });
    pickBrand('UNKNOWN');
    setSavedCardInfo(undefined);

    setIsDeleteSaveCardLoading(false);
    setDeleteSaveCardDialog(false);
  };

  return {
    getSavedCards,
    submitSavedCreditCardPayment,
    submitSavedCreditCardPaymentForInvoice,
    onDeleteSavedCard,
    savedCardInfo,
    isDeleteSaveCardLoading,
    isGetSavedCardsLoading,
  };
};

export default useSavedCreditCard;
